/**
 * useMatterPrefill — BM-H3723
 *
 * Vanessa, 5 Oct 2026: the Invoice / Payment Request buttons on the matter
 * page should open the request with the matter already filled in. The matter
 * page links to `/invoice-request?matter=REF` (and `/payment-request?...`);
 * this reads that ref, fetches the matter's details from
 * GET /api/matters/{ref}/overview and fills only fields that are still empty,
 * so nothing the user has typed is overwritten.
 *
 * Returns a counter that changes once the prefill lands, for use as a `key`
 * on SmartClientMatterSearch (it reads its initialValue only on mount).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { toast } from "sonner";
import { errorMessage } from "../utils/errorMessage";

export default function useMatterPrefill(setFormData, fields) {
  const { search } = useLocation();
  const [prefillKey, setPrefillKey] = useState(0);
  const ref = new URLSearchParams(search).get("matter") || "";

  useEffect(() => {
    if (!ref) return undefined;
    let cancelled = false;
    // The ref alone is enough to start; the rest follows from the read.
    setFormData(prev => (prev.matter_reference ? prev : { ...prev, matter_reference: ref }));
    setPrefillKey(k => k + 1);
    axios.get(`${API}/matters/${encodeURIComponent(ref)}/overview`)
      .then(({ data }) => {
        if (cancelled) return;
        const found = {
          client_name: data.client_name,
          client_email: data.client_email,
          matter_title: data.matter_title,
          responsible_fee_earner: data.fee_earner,
        };
        setFormData(prev => {
          const next = { ...prev, matter_source: prev.matter_source || "" };
          for (const f of fields) {
            if (!next[f] && found[f]) next[f] = found[f];
          }
          return next;
        });
        setPrefillKey(k => k + 1);
      })
      .catch(err => {
        if (!cancelled) toast.error(errorMessage(err, `Could not fill in the details for ${ref}`));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  return prefillKey;
}
